import * as Sentry from '@sentry/react'
import { AlertComponent, LoadingComponent } from 'Components/HelperComponents'
import { EntityForm } from 'NewVersion/components/Forms/EntityForm'
import { GoBackButton } from 'NewVersion/components/UI/GoBack/GoBackButton'
import { ModalInfo } from 'NewVersion/components/UI/Modals/Modal'
import { NewInscriptionIcon } from 'NewVersion/icons/NewInscriptionIcon'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import * as path from 'Routes/Paths'
import { useEdition } from 'hooks/useEdition'
import { useGuillotina } from 'hooks/useGuillotina'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { OBJECT_TYPE_HAVE_RATE } from 'utils/constants'
import { ENS_TYPES, ENS_TYPES_OBJ } from 'utils/entityUtils'
import { get } from 'utils/objectUtils'
import { getCurrentJWToken } from 'utils/utils'

const EditPage = () => {
  // TODO fer valors inicials
  const { entityId, year } = useParams()
  const [searchParams] = useSearchParams()
  const fromFlow = searchParams.get('from')
  const returnRepId = searchParams.get('repId')
  const returnArtistId = searchParams.get('artistId')
  const { currentEdition, edition } = useEdition()
  const [submitModal, setSubmitModal] = useState()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState({
    hasError: false,
    message: '',
  })

  const {
    data: entityDetail,
    isLoading: loading,
    error: errorFetchData,
    refresh: refreshEntity,
  } = useGuillotina({
    path: `${year}/${entityId}`,
  })

  const entityTypeObj = useMemo(() => {
    if (entityDetail) {
      return Object.values(ENS_TYPES_OBJ).find((obj) => {
        if (entityDetail['type_name'] === ENS_TYPES_OBJ.artista.interface) {
          return obj.text === entityDetail.tipus
        }
        return obj.interface === entityDetail['type_name']
      })
    }
    return null
  }, [entityDetail])

  if (loading || !currentEdition || !entityTypeObj || (!entityDetail && !errorFetchData)) {
    return <LoadingComponent />
  }

  if (errorFetchData) {
    return <AlertComponent type="danger"> {t('error-fetching-data')}</AlertComponent>
  }

  const handleEntityError = (error) => {
    const err = new Error(`Edit Entity form error: ${JSON.stringify(get(error, 'info', error))}`)
    err.name = 'Edit Entity form error'
    Sentry.captureException(err)
    if (error && get(error, 'info', null)) {
      if (get(error, 'info.code') === 'invalid-email') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('invalid_email')}`,
        })
      } else if (get(error, 'info.code') === 'promo-code-not-exist') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('promo_code_not_exist')}`,
        })
      } else {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('error_create_or_update_ens')}`,
        })
      }
    } else {
      setSubmitError({
        hasError: true,
        message: `Error: ${t('error_generic')}`,
      })
    }
    window.scrollTo(0, 0)
  }

  const isArtistOrManager = () => {
    return (
      entityTypeObj['interface'] === ENS_TYPES.artist ||
      entityTypeObj['interface'] === ENS_TYPES.manager
    )
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)

    const token = getCurrentJWToken()
    const dataEntity = Object.assign({}, values, {
      '@type': entityTypeObj['interface'],
      tarifa: currentEdition.getCurrentRateByType(
        isArtistOrManager()
          ? OBJECT_TYPE_HAVE_RATE.ens_artist_or_manager
          : OBJECT_TYPE_HAVE_RATE.ens
      ),
    })

    if (dataEntity.pais !== 'country-ES') {
      dataEntity.comunitat_autonoma = null
      dataEntity.comarca = null
    } else if (dataEntity.comunitat_autonoma !== 'catalunya') {
      dataEntity.comarca = null
      dataEntity.comunitat_autonoma = dataEntity.comunitat_autonoma.value
    }

    await fetchGuillotina({
      path: `${edition}/${entityId}`,
      method: 'PATCH',
      data: dataEntity,
      token: token,
    })
      .then(() => {
        if (submitAction) {
          submitAction()
        }
      })
      .catch((error) => {
        handleEntityError(error)
        setSubmitting(false)
        return
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const submitAction = () => {
    refreshEntity()
    setSubmitModal(true)
  }

  const getBackPath = () => {
    if (fromFlow === 'gestio') {
      if (returnRepId) {
        return path.PROPOSALS_DASHBOARD_REP_PATH.replace(':repId', returnRepId)
      }
      if (returnArtistId) {
        return path.PROPOSALS_DASHBOARD_ARTIST_PATH.replace(':artistId', returnArtistId)
      }
      return path.PROPOSALS_GESTIO_PATH
    }
    return path.ACCOUNT_PATH
  }

  const getBackText = () => {
    if (fromFlow === 'gestio') {
      return t('back_to_gestio')
    }
    return t('tornar_perfil')
  }

  const handleGoBack = () => {
    navigate(getBackPath())
  }

  return (
    <>
      <div className="page-wrapper">
        <GoBackButton onClick={handleGoBack} text={getBackText()} />
        <div onClick={handleGoBack} className="cursor-pointer">
          <h4>{t(ENS_TYPES_OBJ[entityTypeObj.text].titleForm, { year: edition })}</h4>
        </div>
        {submitError.hasError && submitError.message && (
          <AlertComponent type="danger"> {submitError.message}</AlertComponent>
        )}
        <div>
          <EntityForm
            entity={entityDetail}
            ensTypeObj={entityTypeObj}
            handleSubmit={handleSubmit}
          />
        </div>
        <ModalInfo
          open={submitModal}
          setOpen={setSubmitModal}
          handleOk={handleGoBack}
          title={t('entity_updated')}
          icon={<NewInscriptionIcon fill={'#273BF3'} />}
        />
      </div>
    </>
  )
}

export default EditPage
